import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";
import { P, FF } from "../../styles/tokens";
import { fmt } from "../../utils/format";
import { catIcon, catColor } from "../../constants/categories";
import { useFinancial } from "../../contexts/FinancialContext";
import { useUI } from "../../contexts/UIContext";

export default function RightPanel() {
  const {
    month, presupuestos, totalIng, totalFij, totalExt,
    ahorroReal, flujoNeto, pieData,
  } = useFinancial();
  const { setDukyOpen } = useUI();

  const bycat = {};
  month.extras.forEach(e => { if (e.cat) bycat[e.cat] = (bycat[e.cat] || 0) + (e.monto || 0); });
  const presupConDatos = presupuestos.filter(p => p.cat && p.limite > 0).map(p => {
    const gasto = bycat[p.cat] || 0;
    const pct = Math.min((gasto / p.limite) * 100, 100);
    const status = pct >= 100 ? "danger" : pct >= 70 ? "warn" : "ok";
    return { ...p, gasto, pct, status };
  }).sort((a, b) => b.pct - a.pct);
  const catEntries = Object.entries(bycat).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div style={{ width:300, borderLeft:`1.5px solid ${P.border}`, padding:"24px 20px", display:"flex", flexDirection:"column", gap:20, overflowY:"auto", background:P.surface, flexShrink:0 }}>
      {/* Duky message */}
      <div style={{ background:P.ingresos.pastel, borderRadius:18, padding:16, border:`1.5px solid ${P.ingresos.tint}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
          <div style={{ width:34, height:34, borderRadius:11, background:P.ingresos.base, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🐿️</div>
          <div style={{ fontSize:12, fontWeight:700, color:P.ingresos.text }}>Duky dice</div>
        </div>
        <div style={{ fontSize:12, color:P.textMd, fontFamily:FF, lineHeight:1.65 }}>
          {totalIng > 0
            ? `${flujoNeto>=0?"¡Buen ritmo!":"Ojo,"} llevas ${fmt(totalFij+totalExt)} en gastos y ${fmt(ahorroReal)} ahorrado este mes. ${flujoNeto>0?`Te quedan ${fmt(flujoNeto)} libres.`:"Revisa tus gastos en ✏️ Editar."}`
            : "¡Bienvenido a Arbo! Empieza registrando tus ingresos y gastos en ✏️ Editar."}
        </div>
        <button onClick={()=>setDukyOpen(true)} style={{ marginTop:12, width:"100%", padding:"9px", borderRadius:12, border:"none", background:P.ingresos.base, color:"#fff", fontSize:12, fontWeight:700, fontFamily:FF, cursor:"pointer" }}>
          Hablar con Duky →
        </button>
      </div>

      {/* Budget progress */}
      {presupConDatos.length > 0 && (
        <div>
          <div style={{ fontSize:13, fontWeight:800, color:P.text, fontFamily:FF, marginBottom:14 }}>Presupuesto</div>
          {presupConDatos.map(p => {
            const t = p.status === "danger" ? P.danger : p.status === "warn" ? P.warn : P.ingresos;
            return (
              <div key={p.id} style={{ marginBottom:14 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                  <div style={{ width:28, height:28, borderRadius:9, background:`${catColor(p.cat)}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>{catIcon(p.cat)}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:P.textMd, fontFamily:FF }}>{p.cat}</span>
                      <span style={{ fontSize:13, fontWeight:900, color:t.text, fontFamily:FF }}>{p.pct.toFixed(0)}%</span>
                    </div>
                    <div style={{ height:7, background:P.bg, borderRadius:99, overflow:"hidden" }}>
                      <div style={{ height:"100%", borderRadius:99, background:p.status==="danger"?P.danger.mid:p.status==="warn"?P.warn.mid:P.ingresos.mid, width:`${p.pct}%`, transition:"width .5s" }} />
                    </div>
                  </div>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:P.textXs, fontFamily:FF, paddingLeft:36 }}>
                  <span>{fmt(p.gasto)} de {fmt(p.limite)}</span>
                  {p.status==="danger"
                    ? <span style={{ color:P.danger.text, fontWeight:700 }}>🚨 +{fmt(p.gasto-p.limite)}</span>
                    : <span style={{ color:P.ingresos.text, fontWeight:600 }}>{fmt(p.limite-p.gasto)} disp.</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Category breakdown */}
      {catEntries.length > 0 && (
        <div>
          <div style={{ fontSize:13, fontWeight:800, color:P.text, fontFamily:FF, marginBottom:12 }}>Extras por categoría</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {catEntries.map(([cat,val])=>(
              <div key={cat} style={{ borderRadius:14, padding:12, border:`1.5px solid ${P.border}`, background:P.bg }}>
                <div style={{ fontSize:18, marginBottom:6 }}>{catIcon(cat)}</div>
                <div style={{ fontSize:10, fontWeight:600, color:P.textSm, fontFamily:FF, marginBottom:3 }}>{cat}</div>
                <div style={{ fontSize:16, fontWeight:900, color:P.extras.text, fontFamily:FF, letterSpacing:"-0.5px" }}>{fmt(val)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pie chart */}
      {pieData.length > 0 && (
        <div>
          <div style={{ fontSize:13, fontWeight:800, color:P.text, fontFamily:FF, marginBottom:12 }}>Distribución</div>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" paddingAngle={4}>
                {pieData.map((e,i)=><Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={v=>fmt(v)} contentStyle={{ background:P.surface, border:`1.5px solid ${P.border}`, borderRadius:12, fontSize:11, fontFamily:FF }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:6 }}>
            {pieData.map(d=>(
              <div key={d.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:d.color }} />
                  <span style={{ fontSize:11, color:P.textSm, fontFamily:FF }}>{d.name}</span>
                </div>
                <span style={{ fontSize:12, fontWeight:900, color:P.text, fontFamily:FF }}>{fmt(d.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
